import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiLock } from "react-icons/fi";
import Modal from "../Modal/Modal";
import PoleFormularza from "../common/PoleFormularza";
import { apiService, zarejestrujObslugePinu } from "../../api/apiService";
import ZmianaPinuAdministratora from "./ZmianaPinuAdministratora";
import { DLUGOSC_PINU, DLUGOSC_DAWNEGO_PINU } from "./dlugoscPinu";
import * as toastModul from "../../context/ToastContext";
import s from "./PinNaZadanie.module.css";

/**
 * PIN ADMINISTRATORA NA ŻĄDANIE — jedno okno dla całej aplikacji.
 *
 * [P2-X6/P2-X7, poprawka 2 przeglądu bezpieczeństwa 2026-09-26] Polecenie właściciela: „wykonaj mi testy w zakresie
 * bezpieczeństwa danych aplikacji ... a jak są błędy, to naprawione”.
 *
 * <b>Co było źle.</b> Serwer wymaga PIN-u od konta rangi administratora na zapisach nadających dostęp (panel uprawnień,
 * skład Zarządu, dostęp do modułu zgłoszeń, obsada desku). PIN dało się podać tylko na bramce CAŁEJ strony (AdminPinGate,
 * sekcja „System i integracje”), więc ten sam zapis z e-teczki (zakładka „Zarząd”) albo z Konfiguratora zgłoszeń kończył
 * się komunikatem „Odśwież stronę i podaj PIN” na ekranie, na którym PIN-u podać się nie da.
 *
 * <b>Teraz.</b> `apiService` przy odpowiedzi 403 `admin_pin_required` woła zarejestrowaną tu obsługę: okno prosi o PIN,
 * serwer (`POST /health/verify-admin-pin`) ustawia to samo podpisane ciasteczko, co bramka strony, a zapis ponawia się
 * sam, JEDEN raz. „Anuluj”, ✕ albo Escape = odmowa z czytelnym komunikatem. Kilka zapisów naraz czeka na JEDNO okno.
 * Klik w tło nie zamyka okna (W-10) — potrząsa nim wspólny Modal.
 */
/* [PIN-ZARZAD, decyzja właściciela 2026-09-27] „pin zostaw tylko dla admina 5 cyfry to jest ok". Długość PIN-u przychodzi
   w odmowie serwera (`dlugoscPinu`: 5, albo 4 dla PIN-u sprzed zmiany). Gdy serwer jej nie podał (starsza odpowiedź),
   okno przyjmuje 4 albo 5 cyfr. Po starym PIN-ie serwer mówi `wymagaZmiany` — zapis ponawia się jak zawsze, a okno
   przechodzi w formularz nowego, 5-cyfrowego PIN-u („Później" zamyka). Konto bez rangi administratora tego okna nie
   widzi nigdy: serwer nie wysyła mu `admin_pin_required`. */
const dlugoscZOdmowy = (dane) => (dane?.dlugoscPinu === DLUGOSC_PINU || dane?.dlugoscPinu === DLUGOSC_DAWNEGO_PINU
    ? dane.dlugoscPinu : null);
const pinGotowy = (pin, dlugosc) => (dlugosc
    ? pin.length === dlugosc
    : pin.length >= DLUGOSC_DAWNEGO_PINU && pin.length <= DLUGOSC_PINU);

/* Atrapy ToastContext w testach innych ekranów nie znają tego hooka — bez niego potwierdzenie się nie pokaże, okno działa. */
const useToastJesliJest = typeof toastModul.useToastOpcjonalny === "function" ? toastModul.useToastOpcjonalny : () => null;

const KOMUNIKAT_BRAK_PINU = "Twoje konto nie ma indywidualnego PIN-u administratora — poproś administratora o nadanie PIN-u "
    + "(Uprawnienia i dostęp → Użytkownicy → PIN i skrzynki).";

const PinNaZadanie = () => {
    const [otwarte, setOtwarte] = useState(false);
    const [pin, setPin] = useState("");
    const [blad, setBlad] = useState("");
    const [trwa, setTrwa] = useState(false);
    // null = serwer nie podał długości (przyjmujemy 4 albo 5 cyfr); 4 albo 5 = dokładnie tyle.
    const [dlugosc, setDlugosc] = useState(null);
    // null = okno PIN-u; napis = po starym PIN-ie okno proponuje nowy, 5-cyfrowy (zdanie z serwera).
    const [zmiana, setZmiana] = useState(null);
    /* [PIN-ZARZAD poprawka 2026-09-28, uwagi przeglądu] Formularz nowego PIN-u dostaje:
       • `rozruch` — tryb z odpowiedzi serwera. BYŁO: okno nie wiedziało, że PIN otworzył PIN SYSTEMOWY, więc zapis „nowego
         PIN-u” zakładał PIERWSZY PIN indywidualny bez zdania, że od tej chwili PIN systemowy przestaje otwierać panel
         pozostałym administratorom (bramka strony to zdanie pokazywała, to okno — nie);
       • `obecnyPin` — PIN podany chwilę wcześniej (serwer wymaga go przy zmianie PIN-u). */
    const [rozruch, setRozruch] = useState(false);
    const obecnyPinRef = useRef("");
    // [PIN-ZARZAD poprawka 2 2026-09-28, uwaga przeglądu] Po zapisie nowego PIN-u — dymek ze zdaniem serwera (patrz niżej).
    const toast = useToastJesliJest();
    // Jedno oczekiwanie naraz: { promise, resolve } — kolejne żądania z 403 dostają TĘ SAMĄ obietnicę.
    const oczekiwanie = useRef(null);

    const zakoncz = useCallback((wynik) => {
        const o = oczekiwanie.current;
        oczekiwanie.current = null;
        setOtwarte(false);
        setPin("");
        setBlad("");
        if (o) o.resolve(wynik);
    }, []);

    useEffect(() => {
        // Atrapy apiService w testach innych ekranów nie znają tej funkcji — bez niej okno po prostu nie działa
        // (zachowanie sprzed poprawki), zamiast wywracać całą aplikację.
        if (typeof zarejestrujObslugePinu !== "function") return undefined;
        const wyrejestruj = zarejestrujObslugePinu((odmowa) => {
            if (oczekiwanie.current) return oczekiwanie.current.promise;
            setDlugosc(dlugoscZOdmowy(odmowa));
            setZmiana(null);
            let resolve;
            const promise = new Promise((r) => { resolve = r; });
            oczekiwanie.current = { promise, resolve };
            setPin("");
            setBlad("");
            setOtwarte(true);
            return promise;
        });
        return () => {
            if (typeof wyrejestruj === "function") wyrejestruj();
            // Okno znika razem z aplikacją — czekający zapis dostaje odmowę, a nie wisi w nieskończoność.
            if (oczekiwanie.current) {
                oczekiwanie.current.resolve(false);
                oczekiwanie.current = null;
            }
        };
    }, []);

    const potwierdz = async (e) => {
        if (e && typeof e.preventDefault === "function") e.preventDefault();
        if (!pinGotowy(pin, dlugosc) || trwa) return;
        setTrwa(true);
        setBlad("");
        try {
            const res = await apiService.verifyAdminPin(pin);
            if (res?.data?.wymaganyIndywidualny) {
                setBlad(res.data?.message || KOMUNIKAT_BRAK_PINU);
                setPin("");
                return;
            }
            if (res?.data?.valid) {
                if (res.data.wymagaZmiany) {
                    // Zapis czekający na PIN rusza od razu — prośba o nowy PIN nie blokuje pracy.
                    const o = oczekiwanie.current;
                    oczekiwanie.current = null;
                    obecnyPinRef.current = pin;
                    setRozruch(res.data.tryb === "Rozruch");
                    setPin("");
                    setZmiana(res.data.message || "");
                    if (o) o.resolve(true);
                    return;
                }
                zakoncz(true);
                return;
            }
            setBlad("Nieprawidłowy PIN. Spróbuj ponownie.");
            setPin("");
        } catch (err) {
            setBlad(err?.response?.status === 429
                ? "Zbyt wiele prób. Odczekaj kilka minut i spróbuj ponownie."
                : "Nie udało się sprawdzić PIN-u. Spróbuj ponownie za chwilę.");
            setPin("");
        } finally {
            setTrwa(false);
        }
    };

    const zamknijZmiane = () => {
        setZmiana(null);
        zakoncz(false);
    };

    if (zmiana !== null) {
        return (
            <Modal isOpen={otwarte} onClose={zamknijZmiane} title="Ustaw nowy PIN administratora" width="440px">
                <ZmianaPinuAdministratora
                    komunikat={zmiana || null}
                    rozruch={rozruch}
                    obecnyPin={obecnyPinRef.current}
                    /* BYŁO: `onZapisano={zamknijZmiane}` — okno znikało bez słowa, a zdanie serwera (co zmieniono, którym PIN-em
                       wchodzić od teraz) szło do kosza. TERAZ: to zdanie jako dymek. */
                    onZapisano={(zdanie) => {
                        zamknijZmiane();
                        if (toast?.showToast) toast.showToast(zdanie, "success", 10);
                    }}
                    onPozniej={zamknijZmiane}
                />
            </Modal>
        );
    }

    const maxCyfr = dlugosc || DLUGOSC_PINU;

    return (
        <Modal isOpen={otwarte} onClose={() => zakoncz(false)} title="Potwierdź PIN administratora" width="440px">
            <form className={s.forma} onSubmit={potwierdz} data-testid="pin-na-zadanie">
                <p className={s.opis}>
                    <FiLock aria-hidden="true" className={s.ikona} />
                    <span>
                        Ta zmiana nadaje dostęp, więc wymaga Twojego PIN-u administratora. Po potwierdzeniu zapis wykona się sam.
                    </span>
                </p>
                <PoleFormularza
                    etykieta="PIN administratora"
                    blad={blad || null}
                    id="pin-na-zadanie-pole"
                    podpowiedz={dlugosc === DLUGOSC_DAWNEGO_PINU
                        ? "Twój PIN ma jeszcze 4 cyfry — po potwierdzeniu ustawisz nowy, 5-cyfrowy."
                        : `PIN ma ${DLUGOSC_PINU} cyfr.`}
                >
                    <input
                        type="password"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={maxCyfr}
                        value={pin}
                        onChange={(ev) => {
                            setBlad("");
                            setPin(ev.target.value.replace(/\D/g, "").slice(0, maxCyfr));
                        }}
                        className={s.pole}
                    />
                </PoleFormularza>
                <div className={s.stopka}>
                    <button type="button" className={s.ghostBtn} onClick={() => zakoncz(false)}>
                        Anuluj
                    </button>
                    <button type="submit" className={s.primaryBtn} disabled={!pinGotowy(pin, dlugosc) || trwa}>
                        {trwa ? "Sprawdzam…" : "Potwierdź"}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default PinNaZadanie;
